import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Flag, Timer } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { useNow, countdown } from "@/hooks/useNow";
import { StakeBox } from "@/components/StakeBox";

const HERO = "https://images.unsplash.com/flagged/photo-1569319388901-605a6d2d1299?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const RaceTrack = ({ race, now, runSeconds }) => {
  const t = Math.min(1, (now - new Date(race.start).getTime()) / (runSeconds * 1000));
  const rank = Object.fromEntries(race.result.map((no, i) => [no, i]));
  return (
    <div data-testid={`race-track-${race.id}`} className="space-y-1.5 rounded-lg border border-[#1E293B] bg-[#0E131F] p-3">
      {race.runners.map((r) => {
        const fin = 100 - rank[r.no] * (40 / race.runners.length);
        const wobble = Math.sin(t * 9 + r.no) * 3 * (1 - t);
        const pos = Math.max(0, Math.min(100, fin * (1 - Math.pow(1 - t, 2)) + wobble));
        return (
          <div key={r.no} className="relative h-6 rounded bg-[#14532d]/40">
            <div className="absolute inset-y-0 right-2 w-px bg-white/40" />
            <div className="absolute top-0.5 flex items-center gap-1 transition-[left] duration-200 ease-linear" style={{ left: `calc(${pos}% - ${pos * 0.9}px)` }}>
              <span className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-black text-[#0B0E14]" style={{ background: r.silk }}>
                {r.no}
              </span>
              <span className="hidden text-[10px] text-slate-300 sm:inline">{r.name}</span>
            </div>
          </div>
        );
      })}
      {t >= 1 && (
        <div className="pt-2 text-sm font-bold text-[#10B981]">
          Winner: #{race.result[0]} {race.runners.find((x) => x.no === race.result[0])?.name}
        </div>
      )}
    </div>
  );
};

const RunnerRow = ({ r, sel, onPick, open }) => (
  <div data-testid={`runner-row-${r.no}`} className="grid grid-cols-[auto_1fr_auto_auto] items-center gap-3 border-b border-[#1E293B] px-3 py-2.5 last:border-0">
    <span className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-black text-[#0B0E14]" style={{ background: r.silk }}>{r.no}</span>
    <div className="min-w-0">
      <div className="truncate text-sm font-semibold">{r.name}</div>
      <div className="text-[11px] text-slate-500">{r.jockey} · form {r.form}</div>
    </div>
    {["win", "place"].map((m) => {
      const active = sel?.runner === r.no && sel?.market === m;
      return (
        <button
          key={m}
          disabled={!open}
          data-testid={`runner-${r.no}-${m}`}
          onClick={() => onPick({ runner: r.no, market: m, odds: r[m], name: r.name })}
          className={`w-[72px] rounded-md border px-2 py-1.5 text-center transition-colors disabled:opacity-40 ${
            active ? "border-[#F59E0B] bg-[#F59E0B]/20 text-[#F59E0B]" : "border-[#1E293B] bg-[#1A2234] hover:border-[#10B981]/60"
          }`}
        >
          <div className="text-[9px] uppercase tracking-wider text-slate-400">{m}</div>
          <div className="font-odds text-sm font-bold">{r[m].toFixed(2)}</div>
        </button>
      );
    })}
  </div>
);

export default function Racing() {
  const qc = useQueryClient();
  const { refresh } = useAuth();
  const { currency } = useCurrency();
  const [track, setTrack] = useState("all");
  const [sel, setSel] = useState(null);
  const { data } = useQuery({ queryKey: ["racing"], queryFn: () => api.get("/racing").then((r) => r.data), refetchInterval: 5000 });
  const offset = useMemo(() => (data ? new Date(data.server_time).getTime() - Date.now() : 0), [data]);
  const now = useNow(250, offset);
  const tracks = data ? [...new Set(data.upcoming.map((r) => r.track))] : [];
  const upcoming = (data?.upcoming || []).filter((r) => (track === "all" || r.track === track) && new Date(r.start).getTime() > now);
  const race = upcoming.find((r) => r.id === sel?.race) || upcoming[0];
  const running = (data?.recent || []).filter((r) => (track === "all" || r.track === track) && r.state !== "open" && now - new Date(r.start).getTime() < (data.run_seconds + 15) * 1000);
  const finished = (data?.recent || []).filter((r) => (track === "all" || r.track === track) && now - new Date(r.start).getTime() >= data.run_seconds * 1000).slice(0, 8);
  const pick = sel && race && sel.race === race.id && sel.market ? sel : null;

  const place = async (stake) => {
    try {
      await api.post("/racing/bet", { race_id: race.id, market: pick.market, runner: pick.runner, odds: pick.odds, stake, currency });
      toast.success(`Bet placed on #${pick.runner} ${pick.name}`);
      setSel(null);
      refresh();
      qc.invalidateQueries({ queryKey: ["bets"] });
      return true;
    } catch (e) {
      toast.error(errMsg(e));
      return false;
    }
  };

  return (
    <div className="space-y-6">
      <section className="grain relative overflow-hidden rounded-xl border border-[#1E293B]">
        <img src={HERO} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14] via-[#0B0E14]/80 to-transparent" />
        <div className="relative space-y-2 p-6 sm:p-8">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#F59E0B]">Virtual racing · a race every minute</span>
          <h1 className="font-display text-4xl font-black uppercase tracking-tight sm:text-5xl">Horse Racing</h1>
          <p className="max-w-lg text-sm text-slate-300">Greyville, Kenilworth, Ascot and Flemington. Bet Win or Place, watch the race live and get paid straight to your wallet.</p>
        </div>
      </section>
      <div className="flex flex-wrap gap-2">
        {["all", ...tracks].map((t) => (
          <button key={t} data-testid={`racing-track-${t}`} onClick={() => { setTrack(t); setSel(null); }}
            className={`rounded-full px-4 py-1.5 text-sm capitalize ${track === t ? "bg-[#10B981] font-bold text-[#0B0E14]" : "bg-[#121824] text-slate-300"}`}>
            {t === "all" ? "All tracks" : t}
          </button>
        ))}
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1">
        {upcoming.slice(0, 8).map((r) => (
          <button key={r.id} data-testid={`race-chip-${r.id}`} onClick={() => setSel({ race: r.id })}
            className={`shrink-0 rounded-lg border px-3 py-2 text-left text-xs ${race?.id === r.id ? "border-[#10B981] bg-[#10B981]/10" : "border-[#1E293B] bg-[#121824]"}`}>
            <div className="font-bold">{r.track_name} R{r.race_no}</div>
            <div className="font-odds text-[#F59E0B]">{countdown(r.start, now)}</div>
          </button>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {race && (
          <section className="overflow-hidden rounded-lg border border-[#1E293B] bg-[#121824]">
            <header className="flex items-center justify-between border-b border-[#1E293B] bg-[#0E131F] px-4 py-3">
              <div>
                <h2 data-testid="race-title" className="font-display text-lg font-black uppercase">{race.track_name} · Race {race.race_no}</h2>
                <div className="text-xs text-slate-400">{race.country} · {race.distance}m · {race.runners.length} runners</div>
              </div>
              <span data-testid="race-countdown" className="flex items-center gap-1.5 rounded bg-[#F59E0B]/15 px-2 py-1 font-odds text-sm font-bold text-[#F59E0B]">
                <Timer className="h-4 w-4" /> {countdown(race.start, now)}
              </span>
            </header>
            {race.runners.map((r) => (
              <RunnerRow key={r.no} r={r} sel={pick} open onPick={(p) => setSel({ ...p, race: race.id })} />
            ))}
          </section>
        )}
        <aside className="space-y-4">
          <div className="rounded-lg border border-[#1E293B] bg-[#121824] p-4">
            <h3 className="mb-2 font-display font-black uppercase">Your selection</h3>
            {pick ? (
              <p data-testid="race-selection" className="mb-3 text-sm">
                #{pick.runner} {pick.name} · <span className="capitalize">{pick.market}</span> @ <span className="font-odds font-bold text-[#10B981]">{pick.odds.toFixed(2)}</span>
              </p>
            ) : (
              <p className="mb-3 text-sm text-slate-400">Tap a Win or Place price.</p>
            )}
            <StakeBox odds={pick?.odds} disabled={!pick} onPlace={place} testid="race-stake" />
            <p className="mt-2 text-[11px] text-slate-500">Place pays if your horse finishes in the top 3.</p>
          </div>
        </aside>
      </div>
      {running.length > 0 && (
        <section className="space-y-3">
          <h2 className="flex items-center gap-2 font-display text-lg font-black uppercase">
            <span className="live-dot h-2 w-2 rounded-full bg-[#EF4444]" /> Racing now
          </h2>
          {running.slice(0, 2).map((r) => (
            <div key={r.id}>
              <div className="mb-1 text-xs text-slate-400">{r.track_name} Race {r.race_no} · {r.distance}m</div>
              <RaceTrack race={r} now={now} runSeconds={data.run_seconds} />
            </div>
          ))}
        </section>
      )}
      <section className="space-y-2">
        <h2 className="font-display text-lg font-black uppercase">Latest results</h2>
        <div className="grid gap-2 md:grid-cols-2">
          {finished.map((r) => (
            <div key={r.id} data-testid={`race-result-${r.id}`} className="flex items-center gap-3 rounded-lg border border-[#1E293B] bg-[#121824] px-3 py-2 text-sm">
              <Flag className="h-4 w-4 text-[#10B981]" />
              <span className="flex-1 truncate">{r.track_name} R{r.race_no}</span>
              {r.result.slice(0, 3).map((no, i) => (
                <span key={no} className="flex items-center gap-1 text-xs">
                  <span className="text-slate-500">{i + 1}.</span>
                  <span className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-black text-[#0B0E14]" style={{ background: r.runners[no - 1].silk }}>{no}</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
